import React, { useState, useEffect } from 'react';
import { 
  MdClose, 
  MdGroup,
  MdPhone,
  MdCheck
} from 'react-icons/md';
import { getPhoneGroups } from '../api/login.service';

interface PhoneGroup {
  id: string;
  name: string;
  description: string;
  totalCount: number;
  createdAt: string;
  updatedAt: string;
}

interface GroupSelectionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (group: PhoneGroup) => void;
  title?: string;
}

export default function GroupSelectionPopup({ 
  isOpen, 
  onClose, 
  onSelect, 
  title = "Select Phone Group" 
}: GroupSelectionPopupProps) {
  const [groups, setGroups] = useState<PhoneGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch phone groups
  const fetchPhoneGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const wabaId = sessionStorage.getItem('wabaId');
      if (!wabaId) {
        setError('No WABA ID found. Please ensure you are logged in.');
        return;
      }

      const response = await getPhoneGroups(wabaId);
      
      // Extract groups from the nested response structure (same as phone-groups-manager)
      const groupsData = response?.data?.groups || [];
      
      if (Array.isArray(groupsData) && groupsData.length > 0) {
        // Transform API response to match our PhoneGroup interface
        const transformedGroups: PhoneGroup[] = groupsData.map((group: any) => ({
          id: group.groupId || group.id || `group_${Date.now()}`,
          name: group.groupName || group.name || 'Unnamed Group',
          description: group.description || '',
          totalCount: group.phoneNumberCount || group.totalCount || group.phoneNumbers?.length || 0,
          createdAt: group.createdAt || new Date().toISOString(),
          updatedAt: group.updatedAt || new Date().toISOString()
        }));
        
        setGroups(transformedGroups);
      } else {
        setError('No phone groups found');
        setGroups([]);
      }
    } catch (error) {
      // Error: 'Error fetching phone groups:', error);
      setError('Error loading phone groups');
      setGroups([]); // Set empty array as fallback
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSelect = (group: PhoneGroup) => {
    onSelect(group);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      fetchPhoneGroups();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-xl font-semibold">{title}</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <MdClose className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchPhoneGroups}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Retry
              </button>
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12">
              <MdGroup className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No phone groups found</p>
              <p className="text-sm text-gray-400">
                Create phone groups in the Phone Groups Manager to use them here
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Array.isArray(groups) && groups.map((group) => (
                <div 
                  key={group.id} 
                  className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors cursor-pointer border-2 border-transparent hover:border-blue-300"
                  onClick={() => handleSelect(group)}
                >
                  {/* Group Info */}
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <MdGroup className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800">{group.name}</h4>
                          <p className="text-sm text-gray-600">{group.description}</p>
                        </div>
                      </div>
                    </div>

                    {/* Group Stats */}
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <MdPhone className="h-4 w-4" />
                        <span>{group.totalCount} numbers</span>
                      </div>
                      <div>
                        <span>Created: {formatDate(group.createdAt)}</span>
                      </div>
                    </div>

                    {/* Action Button */}
                    <div className="pt-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(group);
                        }}
                        className="w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center space-x-2"
                      >
                        <MdCheck className="h-4 w-4" />
                        <span>Select Group</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              {Array.isArray(groups) ? groups.length : 0} phone group{(Array.isArray(groups) ? groups.length : 0) !== 1 ? 's' : ''} available
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
